import {isPlatformBrowser} from '@angular/common';
import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {ContactAttachedData} from '@dcp/configurator';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';

interface SixSenseCompanyDetails {
  company?: {
    country?: string;
    city?: string;
    annual_revenue?: string;
    employee_count?: string;
  };
}

const SIX_SENSE_STORAGE_KEY = '_6senseCompanyDetails';

@Injectable({providedIn: 'root'})
export class ContactAttachedDataService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly campaignTrackingService = inject(CampaignTrackingService);

  getContactAttachedData(): ContactAttachedData {
    if (!isPlatformBrowser(this.platformId)) {
      return this.emptyData();
    }

    const sixSense = this.readSixSenseDetails();
    const campaignData = this.campaignTrackingService.getCampaignData();

    return {
      company_country: sixSense?.company?.country ?? '',
      company_city: sixSense?.company?.city ?? '',
      browser: navigator.userAgent,
      campaigncode: campaignData?.id ?? '',
      device: this.detectDevice(),
      annual_revenue: sixSense?.company?.annual_revenue ?? '',
      employee_count: sixSense?.company?.employee_count ?? '',
    };
  }

  private readSixSenseDetails(): SixSenseCompanyDetails | null {
    try {
      const raw = window.localStorage.getItem(SIX_SENSE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private detectDevice(): string {
    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
    if (navigator.maxTouchPoints > 1 && /macintosh/i.test(userAgent)) return 'tablet';
    if (/android/i.test(userAgent) && !/mobile/i.test(userAgent)) return 'tablet';
    if (/mobile|android|iphone|ipod|blackberry|windows phone/i.test(userAgent)) return 'mobile';
    return 'desktop';
  }

  private emptyData(): ContactAttachedData {
    return {company_country: '', company_city: '', browser: '', campaigncode: '', device: '', annual_revenue: '', employee_count: ''};
  }
}
